import { mat4, ReadonlyMat4 } from 'gl-matrix';
import { RegistrationSnapshot } from '../changes/DomainChange';
import { DicomRegistration } from '../dicom/dicomRegistration';
import { DicomRigidRegistration } from '../dicom/dicomRigidRegistration';
import { domainEventBus } from '../events/DomainEventBus';
import { RegistrationMatrixCommittedEvent } from '../events/RegistrationDomainEvents';
import { ApprovableRegistration } from './approvableRegistration';

export class RigidRegistration extends ApprovableRegistration {
    readonly registrationKind = 'rigid' as const;
    public readonly transformation: mat4;

    /**
     * Session-monotonic counter of committed matrix edits (manual
     * translate/rotate, elastix results, undo/redo restores). A PHI-safe
     * count: comparing it against a baseline detects "matrix changed since
     * load" exactly. Deliberately never decremented — an undo back to the
     * loaded matrix still counts (worst case an identical matrix is
     * re-saved, never a missed save).
     */
    private _transformRevision = 0;

    constructor(
        sourceFrameOfReferenceUid: string,
        targetFrameOfReferenceUid: string,
        transformation: mat4,
        parent: DicomRegistration
    ) {
        super(sourceFrameOfReferenceUid, targetFrameOfReferenceUid, parent);
        this.transformation = transformation;
    }

    captureSnapshot(): RegistrationSnapshot {
        return {
            ...this.snapshotFields(),
            transformRevision: this._transformRevision,
        };
    }

    public get transformRevision(): number {
        return this._transformRevision;
    }

    /**
     * Records a committed matrix edit for dirty tracking. Must run inside a
     * tracker command — call through
     * {@link RegistrationService.commitMatrixEdit}, which the
     * RegistrationEditBridge drives off {@link RegistrationMatrixCommittedEvent}.
     * Throws on an approved registration: the engine guards should have
     * prevented the edit, so reaching here approved is a bug that must
     * surface, never a silently untracked change.
     */
    public markMatrixEdited(): void {
        this.ensureCanModify();
        this._transformRevision++;
        this.tracker?.markChanged(this);
    }

    /**
     * Writes the live matrix into the DICOM dataset and, when the value
     * actually changed, publishes {@link RegistrationMatrixCommittedEvent} —
     * the single dirty-tracking funnel for matrix edits (manual tools commit
     * here on mouse release/keypress; elastix and undo/redo call it too).
     */
    public syncTransformationToDataset(): void {
        if (!(this.dicomParent instanceof DicomRigidRegistration)) {
            return;
        }
        if (this.dicomParent.syncTransformationToDataset()) {
            // Domain id only — PHI-safe (never the SOP UID or label).
            // Synchronous dispatch: the pending-changes classification must
            // reflect the commit before the caller's next read of it.
            domainEventBus.publishSync(
                new RegistrationMatrixCommittedEvent(this.id)
            );
        }
    }

    /**
     * Whether a manual alignment made on the image in `frameOfReferenceUid`
     * writes this registration's own matrix. A pair whose fixed image sits
     * in the target frame is displayed through the inverse matrix, and a
     * drag made from that side would edit the registration backwards.
     */
    public editableFrom(frameOfReferenceUid: string): boolean {
        return this.sourceFrameOfReferenceUid === frameOfReferenceUid;
    }

    /**
     * Returns the transformation matrix that maps points FROM `fromFOR` space
     * INTO `intoFOR` space. Returns null if this registration doesn't link
     * the two frames of reference.
     *
     * @param fromFOR Frame of Reference UID of the coordinate space to transform FROM
     * @param intoFOR Frame of Reference UID of the coordinate space to transform INTO
     */
    public getTransformFromTo(
        fromFOR: string,
        intoFOR: string
    ): ReadonlyMat4 | undefined {
        if (fromFOR === intoFOR) {
            return mat4.create();
        }
        if (
            fromFOR === this.targetFrameOfReferenceUid &&
            intoFOR === this.sourceFrameOfReferenceUid
        ) {
            return this.transformation;
        }
        if (
            fromFOR === this.sourceFrameOfReferenceUid &&
            intoFOR === this.targetFrameOfReferenceUid
        ) {
            // mat4.invert returns null for a singular transform; surface
            // that as "no usable transform" so callers never receive a
            // null outside the ReadonlyMat4 | undefined contract.
            const inverse = mat4.invert(mat4.create(), this.transformation);
            return inverse ?? undefined;
        }
        return undefined;
    }
}

export function isRigidRegistration(
    registration: ApprovableRegistration
): registration is RigidRegistration {
    return registration.registrationKind === 'rigid';
}
